import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  ORDER_STATUS, statusColor, accentColorForType, isStayOrder,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, Chip, Button, Divider, Avatar, IconButton,
  Autocomplete, TextField, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import moment from 'moment/min/moment-with-locales';
import CircularLoading from '../components/CircularLoading';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

const ACTIVE_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.CONFIRMED,
  ORDER_STATUS.IN_PREPARATION,
  ORDER_STATUS.READY,
  ORDER_STATUS.OUT_FOR_DELIVERY,
];

export default function PartnerOrderDetail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const auth = useSelector((s) => s.auth);
  const staticusers = useSelector((s) => s.usersdata?.staticusers) || [];
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [driverAssigning, setDriverAssigning] = useState(false);
  const {
    fetchSinglePartnerOrder, cancelPartnerOrder, updatePartnerOrderStatus,
    assignDriverToPartnerOrder, fetchUsersOnce,
  } = api;

  const reload = async () => {
    const o = await dispatch(fetchSinglePartnerOrder(orderId));
    setOrder(o);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Admin necesita la lista de drivers para asignar.
  useEffect(() => {
    if (auth.profile?.usertype === 'admin' && fetchUsersOnce) {
      dispatch(fetchUsersOnce());
    }
  }, [auth.profile?.usertype, dispatch, fetchUsersOnce]);

  const onCancel = async () => {
    if (!window.confirm(t('cancel_confirm') || 'Cancelar esta reserva?')) return;
    setBusy(true);
    try {
      await dispatch(cancelPartnerOrder(orderId, auth.profile?.uid, ''));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const onAdvance = async (next) => {
    setBusy(true);
    try {
      await dispatch(updatePartnerOrderStatus(orderId, next, auth.profile?.uid));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (!order) return <CircularLoading />;

  const isCustomer = auth.profile?.uid === order.customer;
  const isAdmin = auth.profile?.usertype === 'admin';
  const isPartner = auth.profile?.usertype === 'partner';

  const partnerActions = (() => {
    if (!isPartner && !isAdmin) return [];
    switch (order.status) {
      case ORDER_STATUS.PENDING:
        return [
          { label: t('approve') || 'Aprobar', next: ORDER_STATUS.CONFIRMED, primary: true },
          { label: t('reject') || 'Rechazar', next: ORDER_STATUS.REJECTED },
        ];
      case ORDER_STATUS.CONFIRMED:
        return isStayOrder(order)
          ? [{ label: t('mark_completed') || 'Marcar finalizado', next: ORDER_STATUS.COMPLETED, primary: true }]
          : [{ label: t('mark_in_preparation') || 'En preparación', next: ORDER_STATUS.IN_PREPARATION, primary: true }];
      case ORDER_STATUS.IN_PREPARATION:
        return [{ label: t('mark_ready') || 'Listo', next: ORDER_STATUS.READY, primary: true }];
      case ORDER_STATUS.READY:
        return [{ label: t('out_for_delivery') || 'En camino', next: ORDER_STATUS.OUT_FOR_DELIVERY, primary: true }];
      case ORDER_STATUS.OUT_FOR_DELIVERY:
        return [{ label: t('mark_completed') || 'Entregado', next: ORDER_STATUS.COMPLETED, primary: true }];
      default:
        return [];
    }
  })();

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY, maxWidth: 760, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('order_details') || 'Detalle'} #{order.reference}
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar variant="rounded" src={order.partnerCover || undefined} sx={{ width: 80, height: 80 }}>
            {(order.partnerName || '?').slice(0, 1)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{order.partnerName}</Typography>
              <Chip
                size="small" label={order.partnerType}
                sx={{
                  backgroundColor: `${accentColorForType(order.partnerType)}1a`,
                  color: accentColorForType(order.partnerType),
                  fontWeight: 600,
                }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {moment(order.createdAt).format('lll')}
            </Typography>
          </Box>
          <Chip
            label={order.status}
            sx={{ backgroundColor: `${statusColor(order.status)}1a`, color: statusColor(order.status), fontWeight: 600 }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {isStayOrder(order) ? (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('stay_details') || 'Detalle de estancia'}
            </Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
              <Typography>{t('check_in') || 'Check-in'}</Typography>
              <Typography>{moment(order.stay.checkIn).format('ll')}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{t('check_out') || 'Check-out'}</Typography>
              <Typography>{moment(order.stay.checkOut).format('ll')}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{t('nights') || 'Noches'}</Typography>
              <Typography>{order.stay.nights}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography>{t('guests') || 'Huéspedes'}</Typography>
              <Typography>
                {order.stay.guests?.adults || 1} + {order.stay.guests?.children || 0}
              </Typography>
            </Stack>
            {order.stay.roomSnapshot ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography>{t('room') || 'Habitación'}</Typography>
                <Typography>{order.stay.roomSnapshot.name}</Typography>
              </Stack>
            ) : null}
            {order.stay.requestedRide ? (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#FFF7ED' }}>
                <Typography sx={{ color: MAIN_COLOR, fontWeight: 600 }}>
                  {t('ride_addon_title') || 'Traslado solicitado'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.stay.rideAddress?.add}
                </Typography>
                {order.linkedBookingId ? (
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography variant="caption">
                      {t('linked_booking') || 'Reserva de viaje'}: #{order.linkedBookingId.slice(0, 6)}
                    </Typography>
                    <Button size="small" onClick={() => navigate(`/bookings/bookingdetails/${order.linkedBookingId}`)}>
                      {t('view') || 'Ver'}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('ride_pending_confirmation') || 'Se generará al confirmar la reserva.'}
                  </Typography>
                )}
              </Box>
            ) : null}
          </>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('items') || 'Items'}
            </Typography>
            {(order.purchase?.items || []).map((it) => (
              <Stack key={it.itemId} direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography>{it.qty}× {it.name}</Typography>
                <Typography>{order.currency} {(it.qty * it.price).toFixed(2)}</Typography>
              </Stack>
            ))}
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography>{t('subtotal') || 'Subtotal'}</Typography>
          <Typography>{order.currency} {Number(order.subtotal || 0).toFixed(2)}</Typography>
        </Stack>
        {order.fees ? (
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">{t('fees') || 'Cargos'}</Typography>
            <Typography>{order.currency} {Number(order.fees).toFixed(2)}</Typography>
          </Stack>
        ) : null}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('total') || 'Total'}</Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {order.currency} {Number(order.total || 0).toFixed(2)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap">
          {partnerActions.map((a) => (
            <Button
              key={a.next}
              variant={a.primary ? 'contained' : 'outlined'}
              sx={a.primary ? { backgroundColor: MAIN_COLOR } : {}}
              onClick={() => onAdvance(a.next)}
              disabled={busy}
            >
              {a.label}
            </Button>
          ))}
          {isCustomer && ACTIVE_STATUSES.includes(order.status) && (
            <Button color="error" variant="outlined" onClick={onCancel} disabled={busy}>
              {t('cancel_order') || 'Cancelar reserva'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Panel admin: asignar conductor (solo si es admin y el order tiene
          status activo y NO es estadía — STAY no necesita conductor). */}
      {isAdmin && !isStayOrder(order) && ACTIVE_STATUSES.includes(order.status) ? (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <DirectionsCarIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Conductor
            </Typography>
          </Stack>
          {order.driver ? (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              Asignado: <strong>{order.driverName || order.driver.slice(0, 8)}</strong>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Sin conductor asignado todavía.
            </Alert>
          )}
          <Autocomplete
            options={staticusers.filter(u => u.usertype === 'driver' && u.approved !== false)}
            getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() + (u.mobile ? ` · ${u.mobile}` : '')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Seleccionar conductor" size="small" />}
            onChange={async (_, driver) => {
              if (!driver) return;
              setDriverAssigning(true);
              try {
                await dispatch(assignDriverToPartnerOrder(order.id, driver.id, driver));
                await reload();
              } finally {
                setDriverAssigning(false);
              }
            }}
            disabled={driverAssigning}
          />
          {order.driver && (
            <Button
              size="small" variant="text" color="error" sx={{ mt: 1 }}
              onClick={async () => {
                setDriverAssigning(true);
                try {
                  await dispatch(assignDriverToPartnerOrder(order.id, null, null));
                  await reload();
                } finally {
                  setDriverAssigning(false);
                }
              }}
              disabled={driverAssigning}
            >
              Quitar conductor
            </Button>
          )}
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('timeline') || 'Historial'}
        </Typography>
        <Stack spacing={0.75}>
          {(order.statusHistory || []).map((h, i) => (
            <Stack key={i} direction="row" justifyContent="space-between">
              <Chip
                size="small" label={h.status}
                sx={{ backgroundColor: `${statusColor(h.status)}1a`, color: statusColor(h.status), fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                {moment(h.at).format('lll')}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
